/**
 * @file Fieldset component for finding an entity within its parent.
 * @author Maarten Lensink
 *
 * At the moment it is assumed that the child element has a reference to its parent by a navigation property bearing the same name and/or
 * a property referring to the parent's OID by a property "[parentName]_Oid". The query paremeter "oid" refers to the parent.
 * Label keys next to the input fields are "APP.MISC.[entityName].#SINGULAR"
 * Any deviations from these assumptions require a change in the configuration.
 */
import * as tslib_1 from "tslib";
import { OnDestroy, AfterContentChecked } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FieldsetBase } from '../../base/fieldsets/fieldset-base/fieldset-base';
import { FieldsetProvider } from '../../base/fieldsets/fieldset-provider';
import { DomainService } from '../../domain.service';
import { ComponentRegistry } from '../../core/component-registry';
/**
 * Class for selecting a entity with a parent entity.
 */
var FieldsetParentChildComponent = /** @class */ (function (_super) {
    tslib_1.__extends(FieldsetParentChildComponent, _super);
    /**
     * Constructs a FieldsetEntityComponent.
     */
    function FieldsetParentChildComponent(provider, breezeService) {
        var _this = _super.call(this, provider) || this;
        _this.breezeService = breezeService;
        /**
         * Gets, sets the entity format to find the children of the selected parent.
         * @private
         * @type {EntityFormat}
         */
        _this._childFormat = null;
        /**
         * Gets, sets the entity format to find the all items of the child type.
         * @private
         * @type {EntityFormat}
        * */
        _this._allChildrenFormat = null;
        /**
         * Gets or sets the entity that is shown.
         * @private
         * @type {Entity}
         */
        _this.entity = null;
        /**
         * Field holding the current parent entity.
         * @private
         * @type {Entity}
         */
        _this._parent = null;
        /**
         * Field holding the current child entity.
         * @private
         * @type {Entity}
         */
        _this._child = null;
        /**
         * Subscription reference of property changes of the shown entity.
         * We need this to unsubscripe old subscriptions.
         * @private
         * @type {number}
         */
        _this.subscription = null;
        /**
         * Gets or sets the EntityType of the parent class.
         * We need this to lookup and load the parent.
         * @private
         * @type {EntityType}
         */
        _this.parentType = null;
        /**
         * The label key of the parent entity selector.
         */
        _this.parentLabelKey = '';
        /**
         * The label key of the child entity selector.
         */
        _this.childLabelKey = '';
        _this.parentControl = new FormControl();
        /**
         * The format of the InputEntityComponent that displays the parent.
         */
        _this.parentFormat = null;
        if (provider.field.format) {
            _this.parentFormat = _this.copyFormat(provider.field.format['parent']);
            _this._childFormat = _this.copyFormat(provider.field.format['child']);
            _this._allChildrenFormat = _this.copyFormat(provider.field.format['child']);
            var item = breezeService.getEntityType(_this._allChildrenFormat.entity);
            _this._allChildrenFormat.dataSource = item.defaultResourceName === '' || item.defaultResourceName == null ? item.shortName + 'Items' : item.defaultResourceName;
            _this._allChildrenFormat.parameters = null;
        }
        _this.parentLabelKey = 'APP.MISC.' + _this.parentFormat.entity.toUpperCase() + '.#SINGULAR';
        _this.childLabelKey = 'APP.MISC.' + _this._childFormat.entity.toUpperCase() + '.#SINGULAR';
        _this.parentType = breezeService.getEntityType(_this.parentFormat.entity);
        return _this;
    }
    Object.defineProperty(FieldsetParentChildComponent.prototype, "childFormat", {
        /**
         * The format of the InputEntityComponent that displays the child.
         * @type {EntityFormat}
         */
        get: function () {
            if (this._parent == null) {
                return this._allChildrenFormat;
            }
            return this._childFormat;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetParentChildComponent.prototype, "parent", {
        /**
         * Gets the current parent entity.
         * @type {Entity}
         */
        get: function () {
            return this._parent;
        },
        /**
         * Sets the current parent entity.
         */
        set: function (value) {
            if (this._parent === value) {
                return;
            }
            this._parent = value;
            if (this._parent) {
                var parentOid = this._parent['Oid'];
                this._childFormat.parameters['oid'] = parentOid;
                if (this._child && this._child[this.parentFormat.entity + '_Oid'] !== parentOid) {
                    this._child = null;
                    this.provider.value = null;
                }
                if (this._childFormat.parameters['className']) {
                    this._childFormat.parameters['className'] = this._parent.entityType.shortName;
                }
            }
            else {
                this._childFormat.parameters['oid'] = '';
                if (this._child) {
                    var parentOid = this._child[this.parentFormat.entity + '_Oid'];
                    if (parentOid && parentOid.length !== 0) {
                        this._child = null;
                        this.provider.value = null;
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetParentChildComponent.prototype, "child", {
        /**
         * Gets the current child entity.
         * @type {Entity}
         */
        get: function () {
            return this._child;
        },
        /**
         * Sets the current child entity.
         * It also changes, when needed, the bound property of the shown entity.
         */
        set: function (value) {
            if (value == null) {
                this._child = null;
                // Fix issue #1922, Do I introduce new problems? See code before fix.
                this.provider.value = null;
                // Code before fix issue #1922
                // if (this._child !== this.provider.value) {
                //     this.provider.value = this._child;
                // }
                return;
            }
            if (this._child === value) {
                return;
            }
            if (!(value.entityType && value.entityType.shortName === this._childFormat.entity)) {
                // Value is of wrong type.
                this._child = null;
                return;
            }
            this._child = value;
            if (this._child !== this.provider.value) {
                this.provider.value = this._child;
            }
            if (!this._child) {
                return;
            }
            var parent = this._child[this.parentFormat.entity];
            if (parent) {
                this.parent = parent;
                return;
            }
            var parentOid = this._child[this.parentFormat.entity + '_Oid'];
            if (!parentOid || parentOid.length === 0) {
                this.parent = null;
                return;
            }
            this.getEntity(parentOid);
        },
        enumerable: true,
        configurable: true
    });
    FieldsetParentChildComponent.prototype.getEntity = function (parentOid) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var promise;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.breezeService.getEntityByKey(this.parentType, parentOid).toPromise()];
                    case 1:
                        promise = _a.sent();
                        this.parent = promise;
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Register component by the fieldset provider
     */
    FieldsetParentChildComponent.register = function () {
        ComponentRegistry.register('fieldset', 'parentchild', FieldsetParentChildComponent);
    };
    /**
     * Copies the entity format from the configuration to this component.
     * @private
     * @param {EntityFormat} configFormat The entity format given in the configuration.
     * @returns {EntityFormat} An EntityFormat when configFormat exists otherwise null
     */
    FieldsetParentChildComponent.prototype.copyFormat = function (configFormat) {
        if (!configFormat) {
            return null;
        }
        var result = {};
        Object.keys(configFormat).forEach(function (property) {
            switch (property) {
                case 'parameters':
                    var parameters_1 = configFormat[property];
                    var copyOfParams_1 = {};
                    if (parameters_1) {
                        Object.keys(parameters_1).forEach(function (item) { return copyOfParams_1[item] = parameters_1[item]; });
                    }
                    result[property] = copyOfParams_1;
                    break;
                case 'fields':
                    var array = configFormat[property];
                    var copy_1 = [];
                    if (array) {
                        array.forEach(function (item) { return copy_1.push(item); });
                    }
                    result[property] = copy_1;
                    break;
                default:
                    result[property] = configFormat[property];
            }
        });
        if (!result['parameters']) {
            result['parameters'] = { 'oid': '?' };
        }
        if (!result['fields']) {
            result['fields'] = ['Name'];
        }
        return result;
    };
    /**
     * Lifecycle hook that is called when the component is destroyed.
     */
    FieldsetParentChildComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
    };
    /**
     * Lifecycle hook that is called after every check of the components's content.
     */
    FieldsetParentChildComponent.prototype.ngAfterContentChecked = function () {
        var _this = this;
        if (this.entity !== this.provider.entity) {
            if (this.subscription != null) {
                this.entity.entityAspect.propertyChanged.unsubscribe(this.subscription);
            }
            this.entity = this.provider.entity;
            this.child = this.provider.value;
            if (this.entity) {
                this.subscription = this.entity.entityAspect.propertyChanged.subscribe(function (prop) {
                    if (_this.provider.field && prop.propertyName === _this.provider.field.name) {
                        _this.child = prop.newValue;
                    }
                });
            }
        }
    };
    return FieldsetParentChildComponent;
}(FieldsetBase));
export { FieldsetParentChildComponent };
